import React, { useState, useEffect, useRef, useMemo } from "react";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import {
  searchParticipantsAPI,
  getPreviouslyPlayedParticipantsAPI,
  addAsFriendsAPI,
  removeFromFriendsAPI,
} from "../../api/game";
import { getCharacterListAPI } from "../../api/user";
import { useQuery } from "@tanstack/react-query";
import useToast from "../../hooks/useToast";
import { useTranslation } from "react-i18next";
import SquareShape from "../../assets/images/comman/square-shape.png";
import SquareShape1 from "../../assets/images/comman/square-shape1.png";
import Avatar1 from "../../assets/images/comman/avatar1.png";
import Avatar2 from "../../assets/images/comman/avatar2.png";
import TeamAchievementModal from "./TeamAchievementModal";
import DeleteParticipantModal from "./DeleteParticipantModal";
import type { Participant } from "../../types/participant";
import { socket } from "../../utils/socket";
import { useSelector } from "react-redux";
import type { RootState } from "../../redux/store";
import { getDefaultPortraitByGender, resolveCharacterUrlFromList } from "../../utils/charcter";
import { isInappropriateUsername } from "../../utils/usernameValidation";

interface LocalizedName {
  en: string;
  ar: string;
  _id?: string;
}

interface SearchUser {
  id: string;
  full_name: string;
  email: string;
  phone_number?: string;
  country_code?: string;
  profile_image: string | null;
  gender?: string | null;
  display_category_name?: LocalizedName | null;
  display_achievement_name?: LocalizedName | null;
  IQ?: number | null;
}

interface SearchResult {
  users: SearchUser[];
  previous_participants: SearchUser[];
}

// Custom debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

interface TeamParticipantModalsProps {
  isOpen: boolean;
  onClose: () => void;
  teamIndex: number;
  teamName: string;
  participants: Participant[];
  onUpdateParticipants: (participants: Participant[]) => void;
  otherTeamsParticipants?: Participant[]; // Participants from other teams to prevent duplicates
  roomName: string;
}

const MAX_PARTICIPANTS = 8;

const sanitizeCaptainParticipants = (participants: Participant[]): Participant[] => {
  let captainMarked = false;
  return participants.map((participant) => {
    if (participant.isCaptain && !captainMarked) {
      captainMarked = true;
      return { ...participant, isCaptain: true };
    }
    return { ...participant, isCaptain: false };
  });
};

// Modal Component
const Modal = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
}) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      // Trigger animation after render
      setTimeout(() => setIsAnimating(true), 10);
    } else {
      setIsAnimating(false);
      // Delay unmounting to allow exit animation
      const timer = setTimeout(() => setShouldRender(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  if (!shouldRender) return null;

  return (
    <div
      className={`fixed inset-0 bg-black/80 flex items-center justify-center lg:p-[1%] p-4 z-50 transition-opacity duration-300 ${isAnimating ? 'opacity-100' : 'opacity-0'
        }`}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      {/* Modal Content */}
      <div className={`modal bg-white-500 lg:rounded-[1.5vw] rounded-3xl 2xl:max-w-[42.292vw] md:max-w-[716px] max-w-3xl w-full relative lg:border-[0.2vw] border-3 border-primary auth-page max-h-[calc(100dvh-40px)] lg:max-h-[calc(100dvh-11%)] xl:w-full flex flex-col overflow-hidden transition-all duration-300 ${isAnimating ? 'animate-[slideUp_0.3s_ease-out] opacity-100' : 'opacity-0 translate-y-4'
        }`}>
        {/* Header */}
        <div className="2xl:px-[2vw] md:px-8 px-4 2xl:pt-[2vw] md:pt-8 pt-6 flex-shrink-0">
          <div className="flex 2xl:gap-[1vw] gap-2 items-start justify-between w-full lg:mb-[1vw] mb-3">
            <h1 className="2xl:text-[1.563vw]/[1.7vw] xl:text-3xl/tight text-2xl/6 font-semibold text-primary-orange">
              {title}
            </h1>
            {/* Close Button */}
            <button
              onClick={onClose}
              className="lg:w-[1.2vw] lg:h-[1.2vw] md:w-5 w-4 md:h-5 h-4 flex items-center justify-center cursor-pointer ms-auto hover:scale-110 transition-transform duration-300 outline-none"
            >
              <svg
                className="w-full h-full"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M15.9474 12.1123C15.8856 12.0504 15.8856 11.9496 15.9474 11.8878L23.5029 4.3286C24.149 3.68231 24.149 2.63063 23.5029 1.98429L22.0051 0.48573C21.692 0.172468 21.2758 0 20.833 0C20.3902 0 19.9739 0.172468 19.6609 0.485683L12.1054 8.04493C12.0672 8.08316 12.0235 8.09122 11.9937 8.09122C11.9639 8.09122 11.9203 8.08321 11.8821 8.04493L4.32658 0.485683C4.01351 0.172468 3.59726 0 3.15445 0C2.71164 0 2.29544 0.172468 1.98236 0.485683L0.484547 1.98429C-0.161516 2.63063 -0.161516 3.68231 0.484547 4.3286L8.04001 11.8878C8.10185 11.9496 8.10185 12.0504 8.04001 12.1123L0.484547 19.6714C-0.161469 20.3178 -0.161469 21.3694 0.484547 22.0157L1.98236 23.5143C2.29544 23.8275 2.71168 24 3.15445 24C3.59721 24 4.01351 23.8275 4.32658 23.5143L11.882 15.9551C11.9203 15.9169 11.9638 15.9089 11.9937 15.9089C12.0235 15.9089 12.0671 15.9169 12.1053 15.9552L19.6608 23.5143C19.9739 23.8276 20.3901 24 20.8329 24C21.2758 24 21.692 23.8276 22.005 23.5143L23.5028 22.0158C24.1489 21.3695 24.1489 20.3178 23.5028 19.6715L15.9474 12.1123Z"
                  fill="#333938"
                />
              </svg>
            </button>
          </div>
          {/* Divider */}
          <div className="text-center">
            <hr className="border-0 2xl:h-[0.053vw] h-[1px] w-full [background:linear-gradient(90deg,rgba(11,51,105,0)_0%,#7364FF_50%,rgba(11,51,105,0)_100%)]" />
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto hide-scroll 2xl:px-[2vw] md:px-8 px-4 2xl:py-[1.4vw] md:py-6 py-4">
          {children}
        </div>

        {/* Footer - Fixed at bottom */}
        {footer && (
          <div className="flex-shrink-0 2xl:px-[2vw] md:px-8 px-6 2xl:pb-[2vw] md:pb-8 pb-6 2xl:pt-[1vw] md:pt-4 pt-3">
            {footer}
          </div>
        )}
      </div>
      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

// Validation helpers
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const validateName = (name: string, t: (key: string) => string): string | null => {
  const trimmed = name.trim();
  // if (trimmed?.length < 3) return t("validation.nameMin3");
  if (trimmed?.length > 20) return t("validation.nameMax");
  if (isInappropriateUsername(trimmed)) return t("validation.nameInappropriate");
  return null;
};

// Search Icon Component
const SearchIcon = () => (
  <svg
    className="2xl:w-[1.2vw] 2xl:h-[1.2vw] md:w-5 w-4 md:h-5 h-4"
    width="32"
    height="32"
    viewBox="0 0 32 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M20 20L22 22" stroke="#A09C9B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path
      d="M22.5777 25.3669C21.8074 24.5966 21.8074 23.3478 22.5777 22.5777C23.3478 21.8074 24.5966 21.8074 25.3669 22.5777L28.0889 25.2997C28.8592 26.07 28.8592 27.3188 28.0889 28.0889C27.3188 28.8592 26.07 28.8592 25.2997 28.0889L22.5777 25.3669Z"
      stroke="#A09C9B"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M22.0007 12.6673C22.0007 7.51266 17.822 3.33398 12.6673 3.33398C7.51266 3.33398 3.33398 7.51266 3.33398 12.6673C3.33398 17.822 7.51266 22.0007 12.6673 22.0007C17.822 22.0007 22.0007 17.822 22.0007 12.6673Z"
      stroke="#A09C9B"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Main Component
function TeamParticipantModals({
  isOpen,
  onClose,
  teamIndex,
  teamName,
  participants,
  onUpdateParticipants,
  otherTeamsParticipants = [],
  roomName,
}: TeamParticipantModalsProps) {
  // Local state for editing
  const [localParticipants, setLocalParticipants] = useState<Participant[]>([]);
  // Add state for achievement modal
  const [selectedParticipantId, setSelectedParticipantId] = useState<number | null>(null);
  const [isAchievementModalOpen, setIsAchievementModalOpen] = useState(false);
  const [participantToDelete, setParticipantToDelete] = useState<{ id: number; name: string } | null>(null);
  const capitalizeWords = (str: string): string => {
    return str.replace(/\b\w/g, (char) => char.toUpperCase());
  };
  const host = useSelector((state: RootState) => state.user.user);
  const { data: portraitCharacterListData } = useQuery({
    queryKey: ["characterList", "portrait"],
    queryFn: () => getCharacterListAPI({ type: "portrait" }),
    staleTime: 1000 * 60 * 30,
  });
  const portraitCharacters = (portraitCharacterListData as any)?.data?.characters || [];
  const getPortraitAvatarUrl = (profileImage?: string | null, gender?: string | null) =>
    resolveCharacterUrlFromList(profileImage, portraitCharacters) ||
    getDefaultPortraitByGender(gender, portraitCharacters) ||
    null;
  // Add this handler function
  const handleParticipantClick = (participantId: number) => {
    setSelectedParticipantId(participantId);
    setIsAchievementModalOpen(true);
  };
  const [newParticipant, setNewParticipant] = useState({
    full_name: "",
    email: "",
    phone_number: "",
    country_code: "973", // Default to Bahrain (+973)
    gender: "",
  });
  const [errors, setErrors] = useState<{ [key: string]: { name?: string; email?: string; phone?: string } }>({});

  // Saving/validation state
  const [isSaving, setIsSaving] = useState(false);

  // Search state — each tab has its own query so switching tabs never triggers the other tab's effect
  const [friendsQuery, setFriendsQuery] = useState("");
  const [inviteQuery, setInviteQuery] = useState("");
  const [searchResults, setSearchResults] = useState<SearchResult>({ users: [], previous_participants: [] });
  const [defaultPreviousParticipants, setDefaultPreviousParticipants] = useState<SearchUser[]>([]);
  const [friendsSearchResults, setFriendsSearchResults] = useState<SearchUser[]>([]);
  const [inviteSearchResults, setInviteSearchResults] = useState<SearchUser[]>([]);
  const [isFriendsSearching, setIsFriendsSearching] = useState(false);
  const [isInviteSearching, setIsInviteSearching] = useState(false);
  const [showInviteResults, setShowInviteResults] = useState(false);
  const wasOpenRef = useRef(false);
  const [activeMainTab, setActiveMainTab] = useState<"friends" | "invite" | "guest">("friends");
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const [selectedGuestGender, setSelectedGuestGender] = useState<string | null>(null);
  const [guestGenderError, setGuestGenderError] = useState<string | null>(null);
  const [guestFullName, setGuestFullName] = useState("");
  const [friendsQueryError, setFriendsQueryError] = useState<string | null>(null);
  const [inviteQueryError, setInviteQueryError] = useState<string | null>(null);
  const [removingFriendId, setRemovingFriendId] = useState<string | null>(null);

  // Per-tab debounced queries
  const debouncedFriendsQuery = useDebounce(friendsQuery, 300);
  const debouncedInviteQuery = useDebounce(inviteQuery, 300);
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  // Handle click outside to close invite search dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
        setShowInviteResults(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Friends tab: fetch when its own debounced query changes — no activeMainTab dependency
  useEffect(() => {
    const fetchFriendsResults = async () => {
      const trimmedQuery = debouncedFriendsQuery.trim();

      if (trimmedQuery.length < 2) {
        setFriendsSearchResults(defaultPreviousParticipants);
        return;
      }

      setIsFriendsSearching(true);
      try {
        const response = await getPreviouslyPlayedParticipantsAPI(trimmedQuery);
        const users = (response?.data?.users ?? []) as SearchUser[];
        setFriendsSearchResults(users);
      } catch (error) {
        console.error("Friends search error:", error);
        setFriendsSearchResults([]);
      } finally {
        setIsFriendsSearching(false);
      }
    };

    fetchFriendsResults();
  }, [debouncedFriendsQuery, defaultPreviousParticipants, localParticipants.length]);

  // Invite tab: fetch when its own debounced query changes — no activeMainTab dependency
  useEffect(() => {
    const fetchInviteResults = async () => {
      const trimmedQuery = debouncedInviteQuery.trim();

      if (trimmedQuery.length < 2) {
        setInviteSearchResults([]);
        setShowInviteResults(false);
        return;
      }

      setIsInviteSearching(true);
      try {
        const response = await searchParticipantsAPI(trimmedQuery);
        const data = response?.data || { users: [], previous_participants: [] };
        setInviteSearchResults(data.users ?? []);
        setSearchResults(data);
        setShowInviteResults(true);
      } catch (error) {
        console.error("Invite search error:", error);
        setInviteSearchResults([]);
      } finally {
        setIsInviteSearching(false);
      }
    };

    fetchInviteResults();
  }, [debouncedInviteQuery]);

  // Sync local state with props when modal opens; fetch default previously played participants
  useEffect(() => {
    const justOpened = isOpen && !wasOpenRef.current;

    if (justOpened) {
      setLocalParticipants(sanitizeCaptainParticipants([...participants]));
      setNewParticipant({ full_name: "", email: "", phone_number: "", country_code: "973", gender: "" });
      setErrors({});
      setFriendsQuery("");
      setInviteQuery("");
      setFriendsQueryError(null);
      setInviteQueryError(null);
      setSearchResults({ users: [], previous_participants: [] });
      setDefaultPreviousParticipants([]);
      setFriendsSearchResults([]);
      setInviteSearchResults([]);
      setShowInviteResults(false);
      setActiveMainTab("friends");
      setSelectedGuestGender(null);
      setGuestGenderError(null);
      setGuestFullName("");

      const fetchDefaultParticipants = async () => {
        try {
          const response = await getPreviouslyPlayedParticipantsAPI();
          const users = (response?.data?.users ?? []) as SearchUser[];
          setDefaultPreviousParticipants(users);
          setFriendsSearchResults(users);
          setSearchResults((prev) => ({ ...prev, previous_participants: users }));
        } catch (error) {
          console.error("Error fetching previously played participants:", error);
        }
      };
      fetchDefaultParticipants();
    } else if (isOpen) {
      // Keep participants in sync without resetting tab/search state while modal remains open.
      setLocalParticipants(sanitizeCaptainParticipants([...participants]));
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, participants]);

  // Check if participant exists in other teams (by user_id, email, or phone)
  const isParticipantInOtherTeams = (userId?: string, email?: string, phone?: string): boolean => {
    return otherTeamsParticipants.some((p) => {
      // Check by user_id first (most reliable for API users)
      if (userId && p.user_id && p.user_id === userId) return true;
      // Check by email if both have emails
      if (email && email.trim() && p.email && p.email.trim() && p.email.toLowerCase() === email.toLowerCase())
        return true;
      // Check by phone if both have phones
      if (phone && phone.trim() && p.phone_number && p.phone_number.trim() && p.phone_number === phone) return true;
      return false;
    });
  };

  // Check if participant exists in current team (by user_id, email, or phone)
  const isParticipantInCurrentTeam = (userId?: string, email?: string, phone?: string): boolean => {
    return localParticipants.some((p) => {
      // Check by user_id first
      if (userId && p.user_id && p.user_id === userId) return true;
      // Check by email if both have emails
      if (email && email.trim() && p.email && p.email.trim() && p.email.toLowerCase() === email.toLowerCase())
        return true;
      // Check by phone if both have phones
      if (phone && phone.trim() && p.phone_number && p.phone_number.trim() && p.phone_number === phone) return true;
      return false;
    });
  };

  // Check if participant exists in ANY team (current or other teams)
  const isParticipantInAnyTeam = (userId?: string, email?: string, phone?: string): boolean => {
    return isParticipantInCurrentTeam(userId, email, phone) || isParticipantInOtherTeams(userId, email, phone);
  };

  // Remove participant from current team (by user_id, email, or phone)
  const removeParticipantFromSearch = (userId?: string, email?: string, phone?: string) => {
    setLocalParticipants(
      localParticipants?.filter((p) => {
        // Check by user_id first
        if (userId && p.user_id && p.user_id === userId) return false;
        // Check by email if both have emails
        if (email && email.trim() && p.email && p.email.trim() && p.email.toLowerCase() === email.toLowerCase())
          return false;
        // Check by phone if both have phones
        if (phone && phone.trim() && p.phone_number && p.phone_number.trim() && p.phone_number === phone) return false;
        return true;
      })
    );
  };

  const inviteParticipant = (user: SearchUser) => {
    socket.emit("invite_participant", { participant_id: user.id, user_id: host?.id, roomName: roomName });
  };

  const addAsFriendFromSearch = async (user: SearchUser) => {
    try {
      const response = await addAsFriendsAPI({ friend_id: user.id });
      useToast(
        response?.message ||
        t("createGame.teamParticipantModal.friendRequestSent", {
          name: user.full_name,
        }),
        "success"
      );
      addUserFromSearch(user);
    } catch (error: any) {
      useToast(error?.message || t("createGame.teamParticipantModal.friendRequestFailed"), "error");
    }
  };

  const removeFriendFromFriends = async (user: SearchUser) => {
    if (removingFriendId) return;
    setRemovingFriendId(user.id);
    try {
      const response = await removeFromFriendsAPI({ friend_id: user.id });
      useToast(
        response?.message ||
        t("createGame.teamParticipantModal.removedFromFriends", {
          name: user.full_name,
        }),
        "success"
      );
      setDefaultPreviousParticipants((prev) => prev.filter((u) => u.id !== user.id));
      setFriendsSearchResults((prev) => prev.filter((u) => u.id !== user.id));
      setSearchResults((prev) => ({
        ...prev,
        previous_participants: (prev.previous_participants ?? []).filter((u) => u.id !== user.id),
      }));
    } catch (error: any) {
      useToast(error?.message || t("createGame.teamParticipantModal.removeFromFriendsFailed"), "error");
    } finally {
      setRemovingFriendId(null);
    }
  };

  const onUpdateParticipantsRef = useRef(onUpdateParticipants);
  onUpdateParticipantsRef.current = onUpdateParticipants;

  // useEffect(() => {
  //   socket.on("participant_joined", (data: any) => {
  //     const participantId = data?.participant_id as string | undefined;
  //     if (!participantId) return;
  //     const pid = String(participantId).trim();

  //     setLocalParticipants((prev) => {
  //       const next = prev.map((p) =>
  //         String(p.user_id ?? "").trim() === pid ? { ...p, joinStatus: "joined" as const } : p
  //       );
  //       const didUpdate = next.some((p, i) => p.joinStatus === "joined" && prev[i]?.joinStatus !== "joined");
  //       if (didUpdate) {
  //         onUpdateParticipantsRef.current(next);
  //       }
  //       return next;
  //     });
  //   });
  //   return () => {
  //     socket.off("participant_joined");
  //   };
  // }, [socket.connected]);

  // Add user from search results
  const addUserFromSearch = (user: SearchUser) => {
    // Check if user already exists in current team's participants
    if (isParticipantInCurrentTeam(user.id, user.email, user.phone_number)) {
      return; // Already added to this team
    }

    // Check if user exists in other teams
    if (isParticipantInOtherTeams(user.id, user.email, user.phone_number)) {
      useToast(t("createGame.teamParticipantModal.participantAlreadyInAnotherTeam"), "error");
      return;
    }

    // Check if adding would exceed the maximum limit
    if (localParticipants?.length >= MAX_PARTICIPANTS) {
      useToast(
        t("createGame.teamParticipantModal.maxParticipantsReached", { max: MAX_PARTICIPANTS }) ||
        `Maximum ${MAX_PARTICIPANTS} participants allowed per team`,
        "error"
      );
      return;
    }

    // Do not emit invite event if the participant is the host themself
    const isSelfInvite = (host && String(user.id) === String(host.id)) ? true : false;

    if (!isSelfInvite) {
      inviteParticipant(user);
    }

    const newParticipant: Participant = {
      id: Date.now(),
      user_id: user.id, // Store the API user ID for existing users
      full_name: user.full_name,
      email: user.email || "",
      phone_number: user.phone_number || "",
      country_code: user.country_code || "",
      gender: user.gender || null,
      isFromAPI: true, // Mark as added from API - fields should not be editable
      profile_image: user.profile_image,
      display_category_name: user.display_category_name ?? undefined,
      display_achievement_name: user.display_achievement_name ?? undefined,
      invitedAt: new Date().toISOString(),
      IQ: user.IQ || null,
      joinStatus: isSelfInvite ? "joined" : "invited",
      // Track which fields were originally filled from API
      apiFieldsFilled: {
        full_name: !!user.full_name,
        email: !!user.email,
        phone_number: !!user.phone_number,
        country_code: !!user.country_code,
      },
      isCaptain: localParticipants.length === 0,
    };

    const updatedParticipants = sanitizeCaptainParticipants([...localParticipants, newParticipant]);

    // Update local state so the modal shows the participant immediately
    setLocalParticipants(updatedParticipants);
    // Also update parent state immediately so the participant persists
    onUpdateParticipants(updatedParticipants);
    // Clear whichever tab's search was active
    if (activeMainTab === "friends") {
      setFriendsQuery("");
    } else {
      setInviteQuery("");
      setShowInviteResults(false);
    }
  };

  const removeParticipant = (id: number) => {
    const updatedParticipants = sanitizeCaptainParticipants(localParticipants?.filter((p) => p.id !== id));
    setLocalParticipants(updatedParticipants);
    onUpdateParticipants(updatedParticipants);
  };

  const assignCaptain = (id: number) => {
    const selected = localParticipants.find((participant) => participant.id === id);
    if (!selected) return;

    const reorderedParticipants = sanitizeCaptainParticipants([
      { ...selected, isCaptain: true },
      ...localParticipants.filter((participant) => participant.id !== id).map((participant) => ({
        ...participant,
        isCaptain: false,
      })),
    ]);

    setLocalParticipants(reorderedParticipants);
    onUpdateParticipants(reorderedParticipants);
  };

  const handleConfirmDeleteParticipant = () => {
    if (participantToDelete !== null) {
      removeParticipant(participantToDelete.id);
      setParticipantToDelete(null);
    }
  };

  const handleCancelDeleteParticipant = () => {
    setParticipantToDelete(null);
  };

  const handleSave = async () => {
    // Validate new participant if it has values
    let finalParticipants = [...localParticipants];
    const hasNewParticipantData =
      newParticipant.full_name.trim() || newParticipant.email.trim() || newParticipant.phone_number.trim();

    if (hasNewParticipantData) {
      // Check if adding would exceed the maximum limit
      if (finalParticipants?.length >= MAX_PARTICIPANTS) {
        useToast(
          t("createGame.teamParticipantModal.maxParticipantsReached", { max: MAX_PARTICIPANTS }) ||
          `Maximum ${MAX_PARTICIPANTS} participants allowed per team`,
          "error"
        );
        return;
      }

      // If validation passes, add the new participant
      finalParticipants.push({
        id: Date.now(),
        full_name: newParticipant.full_name.trim(),
        email: newParticipant.email.trim(),
        phone_number: newParticipant.phone_number.trim() || undefined,
        country_code: newParticipant.country_code.trim() || undefined,
        gender: newParticipant.gender || null,
        profile_image: null,
        invitedAt: new Date().toISOString(),
        joinStatus: "invited",
        isCaptain: finalParticipants.length === 0,
      });
    }

    // Check final count doesn't exceed limit (shouldn't happen, but safety check)
    if (finalParticipants?.length > MAX_PARTICIPANTS) {
      useToast(
        t("createGame.teamParticipantModal.maxParticipantsReached", { max: MAX_PARTICIPANTS }) ||
        `Maximum ${MAX_PARTICIPANTS} participants allowed per team`,
        "error"
      );
      return;
    }

    setIsSaving(true);
    onUpdateParticipants(sanitizeCaptainParticipants(finalParticipants));
    onClose();
    setIsSaving(false);
    return;
  };

  // Filter Friends tab results (recent/search) to exclude participants already in any team
  const filteredFriendsList = useMemo(() => {
    const source = friendsQuery.trim().length >= 2 ? friendsSearchResults : defaultPreviousParticipants;
    return source?.filter((user) => {
      const inCurrentTeam = localParticipants.some((p) => {
        if (user.id && p.user_id && p.user_id === user.id) return true;
        if (
          user.email &&
          user.email.trim() &&
          p.email &&
          p.email.trim() &&
          p.email.toLowerCase() === user.email.toLowerCase()
        )
          return true;
        if (
          user.phone_number &&
          user.phone_number.trim() &&
          p.phone_number &&
          p.phone_number.trim() &&
          p.phone_number === user.phone_number
        )
          return true;
        return false;
      });
      const inOtherTeams = otherTeamsParticipants.some((p) => {
        if (user.id && p.user_id && p.user_id === user.id) return true;
        if (
          user.email &&
          user.email.trim() &&
          p.email &&
          p.email.trim() &&
          p.email.toLowerCase() === user.email.toLowerCase()
        )
          return true;
        if (
          user.phone_number &&
          user.phone_number.trim() &&
          p.phone_number &&
          p.phone_number.trim() &&
          p.phone_number === user.phone_number
        )
          return true;
        return false;
      });
      return !inCurrentTeam && !inOtherTeams;
    }) ?? [];
  }, [friendsSearchResults, defaultPreviousParticipants, friendsQuery, otherTeamsParticipants, localParticipants]);

  // Filter Invite tab results to exclude participants already in any team
  const filteredInviteList = useMemo(() => {
    return inviteSearchResults?.filter((user) => {
      const inCurrentTeam = localParticipants.some((p) => {
        if (user.id && p.user_id && p.user_id === user.id) return true;
        if (
          user.email &&
          user.email.trim() &&
          p.email &&
          p.email.trim() &&
          p.email.toLowerCase() === user.email.toLowerCase()
        )
          return true;
        if (
          user.phone_number &&
          user.phone_number.trim() &&
          p.phone_number &&
          p.phone_number.trim() &&
          p.phone_number === user.phone_number
        )
          return true;
        return false;
      });
      const inOtherTeams = otherTeamsParticipants.some((p) => {
        if (user.id && p.user_id && p.user_id === user.id) return true;
        if (
          user.email &&
          user.email.trim() &&
          p.email &&
          p.email.trim() &&
          p.email.toLowerCase() === user.email.toLowerCase()
        )
          return true;
        if (
          user.phone_number &&
          user.phone_number.trim() &&
          p.phone_number &&
          p.phone_number.trim() &&
          p.phone_number === user.phone_number
        )
          return true;
        return false;
      });
      return !inCurrentTeam && !inOtherTeams;
    }) ?? [];
  }, [inviteSearchResults, otherTeamsParticipants, localParticipants]);

  const displayTitle = teamName || t("createGame.teamParticipantModal.team", { index: teamIndex + 1 });

  const hasFriendsResults = filteredFriendsList?.length > 0;
  const hasInviteResults = filteredInviteList?.length > 0;

  // Add guest from Guest tab form (direct add, no invite)
  const addGuestFromForm = () => {
    const name = guestFullName.trim();

    if (!name) return;
    setGuestGenderError(null);

    // Validate gender selection
    if (!selectedGuestGender) {
      setGuestGenderError(t("createGame.teamParticipantModal.pleaseSelectAGender"));
      return;
    }

    const nameError = validateName(name, t);
    if (nameError) {
      useToast(nameError, "error");
      return;
    }

    if (localParticipants?.length >= MAX_PARTICIPANTS) {
      useToast(
        t("createGame.teamParticipantModal.maxParticipantsReached", { max: MAX_PARTICIPANTS }) ||
        `Maximum ${MAX_PARTICIPANTS} participants allowed per team`,
        "error"
      );
      return;
    }

    const newParticipant: Participant = {
      id: Date.now(),
      user_id: null,
      full_name: name,
      email: "",
      phone_number: undefined,
      country_code: undefined,
      gender: selectedGuestGender,
      profile_image: null,
      isFromAPI: false,
      invitedAt: new Date().toISOString(),
      joinStatus: "joined",
      isCaptain: localParticipants.length === 0,
    };

    setLocalParticipants((prev) => {
      const next = sanitizeCaptainParticipants([...prev, newParticipant]);
      onUpdateParticipants(next);
      return next;
    });
    setGuestFullName("");
    setSelectedGuestGender(null);
    setGuestGenderError(null);
  };

  const saveButton = (
    <button
      onClick={handleSave}
      disabled={isSaving}
      className={`w-full cursor-pointer h-full flex skew-x-[-10deg] 2xl:border-[0.104vw] border-[1.5px] lg:text-[1.1vw]/[1.4vw] md:text-lg/tight text-base/tight font-bold text-primary-yellow bg-primary-yellow border-black-300 transition-all duration-200 text-center outline-none 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706] ${isSaving ? "opacity-70 cursor-not-allowed" : "hover:-translate-y-0.5"}`}
    >
      <span className="2xl:pt-[0.7vw] pt-2.5 2xl:m-[0.208vw] m-1 !mt-0 2xl:py-[0.5vw] py-2 lg:px-[5%] px-4 2xl:rounded-[0.26vw] rounded-[5px] skew-x-[0deg] !border-t-0 2xl:border-[0.104vw] border-[1.5px] border-black-300 bg-[linear-gradient(180deg,#D76A4D_0%,#B35840_101.89%)] w-full h-full">
        <span className="skew-x-[10deg] text-white flex items-center justify-center gap-2">
          {isSaving ? (
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
          ) : null}
          {t("createGame.teamParticipantModal.save")}
        </span>
      </span>
    </button>
  );

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} title={displayTitle} footer={saveButton}>
        {/* Tab Headers - MUI Tabs for smooth animated indicator */}
        <Tabs
          value={activeMainTab}
          onChange={(_e, val) => setActiveMainTab(val)}
          variant="fullWidth"
          sx={{
            mb: "16px",
            borderBottom: "1.5px solid #110706",
            boxShadow: "0px 2px 0px #110706",
            minHeight: "unset",
            "& .MuiTabs-flexContainer": {
              gap: "4px",
            },
            "& .MuiTabs-indicator": {
              height: "3px",
              backgroundColor: "#d76a4d",
              borderRadius: "2px 2px 0 0",
              transition: "all 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
            },
            "& .MuiTab-root": {
              fontFamily: "inherit",
              fontWeight: 700,
              fontSize: "1.125rem",
              textTransform: "none",
              color: "#a09c9b",
              minHeight: "unset",
              padding: "10px 16px",
              border: "1.5px solid transparent",
              borderBottom: "none",
              borderRadius: "6px 6px 0 0",
              // transform: "skewX(-10deg)",
              transition: "background-color 0.2s, color 0.2s, border-color 0.2s",
              "&:hover:not(.Mui-selected)": {
                backgroundColor: "#f3e8d4",
                color: "#413938",
                borderColor: "#110706",
              },
              // "& .tab-label": { display: "inline-block", transform: "skewX(10deg)" },
            },
            "& .MuiTab-root.Mui-selected": {
              color: "#d76a4d",
              backgroundColor: "#fffba1",
              borderColor: "#110706",
              borderBottom: "none",
            },
            "& .MuiTouchRipple-root": { color: "#d76a4d40" },
          }}
        >
          <Tab
            value="friends"
            label={<span className="tab-label 2xl:text-[1.05vw] text-lg font-bold">{t("createGame.teamParticipantModal.friendsTab")}</span>}
          />
          <Tab
            value="invite"
            label={<span className="tab-label 2xl:text-[1.05vw] text-lg font-bold">{t("createGame.teamParticipantModal.inviteTab")}</span>}
          />
          <Tab
            value="guest"
            label={<span className="tab-label 2xl:text-[1.05vw] text-lg font-bold">{t("createGame.teamParticipantModal.guestTab")}</span>}
          />
        </Tabs>

        {/* Tab Content - rendered conditionally; each tab has its own isolated state */}
        {/* Friends Tab */}
        {activeMainTab === "friends" && (
          <div className="2xl:mb-[1.5vw] mb-4">
            {/* Search bar */}
            <div className="relative 2xl:py-[0.6vw] py-2 2xl:px-[2%] px-4 flex 2xl:rounded-[0.26vw] rounded-[5px] skew-x-[-10deg] 2xl:border-[0.104vw] border-[1.5px] transition-all border-black-300 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706] 2xl:gap-[0.417vw] gap-2 items-center">
              <div className="relative pointer-events-none skew-x-[10deg]">
                <SearchIcon />
              </div>
              <input
                type="text"
                placeholder={t("createGame.teamParticipantModal.searchFriends")}
                dir={isRtl ? "rtl" : "ltr"}
                value={friendsQuery}
                maxLength={21}
                onChange={(e) => {
                  if (e.target.value?.length > 20) {
                    setFriendsQueryError(t("createGame.teamParticipantModal.maxLength", { max: 20 }));
                    return;
                  }
                  setFriendsQuery(capitalizeWords(e.target.value));
                  setFriendsQueryError(null);
                }}
                className="2xl:mt-[0.208vw] mt-1 rtl:dir-rtl rtl:text-right w-full 2xl:text-[1.2vw]/none md:text-lg/none text-base/none font-bold skew-x-[10deg] text-black-300 placeholder:text-black-400 focus:outline-none transition-all disabled:opacity-50 bg-transparent"
              />
              {isFriendsSearching && (
                <div className="absolute right-4 rtl:right-auto rtl:left-4 top-1/2 -translate-y-1/2">
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-secondary border-t-transparent"></div>
                </div>
              )}
            </div>
            {friendsQueryError && (
              <p className="text-red-500 lg:text-[0.9vw] text-xs lg:mt-[0.208vw] mt-1 font-bold">{friendsQueryError}</p>
            )}

            {/* Inline list — always visible, no dropdown */}
            <div className="2xl:mt-[0.75vw] mt-3">
              <p className="2xl:text-[0.95vw] text-base font-bold text-black-400 2xl:mb-[0.5vw] mb-2 uppercase tracking-wider">
                {t("createGame.teamParticipantModal.recentPlayers")}
              </p>
              <div className="2xl:space-y-[0.5vw] space-y-2 max-h-[240px] overflow-y-auto scroll-width-small">
                {isFriendsSearching && friendsQuery.trim().length >= 2 ? (
                  <div className="py-8 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-orange border-t-transparent"></div>
                  </div>
                ) : filteredFriendsList.length === 0 ? (
                  <p className="2xl:text-[1.15vw] text-lg text-black-400 py-4 text-center">
                    {friendsQuery.trim().length >= 2
                      ? t("createGame.teamParticipantModal.noRecordFound", { name: friendsQuery })
                      : t("createGame.teamParticipantModal.noRecentPlayers")}
                  </p>
                ) : (
                  filteredFriendsList.map((user) => {
                    const isAlreadyAdded = isParticipantInCurrentTeam(user.id, user.email, user.phone_number);
                    const isRemovingThis = removingFriendId === user.id;
                    const characterUrl = getPortraitAvatarUrl(user?.profile_image, user?.gender);
                    return (
                      <div
                        key={user.id}
                        title={isAlreadyAdded ? t("createGame.teamParticipantModal.added") || "Already added" : ""}
                        className={`flex w-full items-center 2xl:gap-[0.75vw] gap-2 2xl:p-[0.625vw] p-2 2xl:rounded-[0.26vw] rounded-[5px] transition-all duration-200 2xl:border-[0.104vw] border-[1.5px] border-black-300 ${isAlreadyAdded
                          ? "cursor-default bg-[#E7E6E6] opacity-60"
                          : "bg-primary-ivory 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]"
                          }`}
                      >
                        <button
                          type="button"
                          onClick={() => !isAlreadyAdded && addUserFromSearch(user)}
                          disabled={isAlreadyAdded}
                          className={`flex min-w-0 flex-1 items-center 2xl:gap-[0.75vw] gap-3 text-start ${isAlreadyAdded ? "cursor-not-allowed" : "cursor-pointer hover:opacity-90"}`}
                        >
                          <div className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-[5px] border-[1.5px] border-black-300 bg-[#97A4F2] text-base font-bold text-white 2xl:h-[2.6vw] 2xl:w-[2.6vw] 2xl:rounded-[0.26vw] 2xl:border-[0.104vw] 2xl:text-[0.938vw]">
                            {
                              characterUrl ? (
                                <img src={characterUrl} alt={user.full_name} className="h-full w-full object-cover" />
                              )
                                : user.full_name?.charAt(0)?.toUpperCase() || "?"
                            }
                          </div>
                          <div className="min-w-0 flex-1 text-left">
                            <p className="truncate text-base font-semibold text-primary-orange 2xl:text-[1.02vw]">{user.full_name}</p>
                          </div>
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeFriendFromFriends(user);
                          }}
                          disabled={isRemovingThis}
                          title={t("createGame.teamParticipantModal.removeAsFriend")}
                          aria-label={t("createGame.teamParticipantModal.removeAsFriend")}
                          className="flex h-8 shrink-0 items-center justify-center gap-2 rounded-[5px] border border-black-300 bg-[#E7E6E6] px-3 text-xs font-bold text-primary-rose transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50 2xl:h-[1.875vw] 2xl:gap-[0.35vw] 2xl:rounded-[0.26vw] 2xl:px-[0.625vw] 2xl:text-[0.729vw]"
                        >
                          {isRemovingThis ? (
                            <span className="inline-block h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-primary-orange border-t-transparent" />
                          ) : (
                            <>
                              <svg
                                className="h-4 w-4 shrink-0 2xl:h-[0.833vw] 2xl:w-[0.833vw]"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                aria-hidden="true"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                />
                              </svg>
                              <span className="whitespace-nowrap">{t("createGame.teamParticipantModal.removeAsFriend")}</span>
                            </>
                          )}
                        </button>
                        {!isAlreadyAdded && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              addUserFromSearch(user);
                            }}
                            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[5px] bg-primary-orange 2xl:h-[1.875vw] 2xl:w-[1.875vw] 2xl:rounded-[0.26vw]"
                            aria-label={t("createGame.teamParticipantModal.add") || "Add"}
                          >
                            <svg
                              className="h-4 w-4 skew-x-[10deg] text-white 2xl:h-[0.833vw] 2xl:w-[0.833vw]"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                            </svg>
                          </button>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        )}

        {/* Invite Tab */}
        {activeMainTab === "invite" && (
          <div className="2xl:mb-[1.5vw] mb-4 relative" ref={searchContainerRef}>
            <div className="relative 2xl:py-[0.6vw] py-2 2xl:px-[2%] px-4 flex 2xl:rounded-[0.26vw] rounded-[5px] skew-x-[-10deg] 2xl:border-[0.104vw] border-[1.5px] transition-all border-black-300 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706] 2xl:gap-[0.417vw] gap-2 items-center">
              <div className="relative pointer-events-none skew-x-[10deg]">
                <SearchIcon />
              </div>
              <input
                type="text"
                placeholder={t("createGame.teamParticipantModal.searchAllUsers")}
                dir={isRtl ? "rtl" : "ltr"}
                value={inviteQuery}
                maxLength={21}
                onChange={(e) => {
                  if (e.target.value?.length > 20) {
                    setInviteQueryError(t("createGame.teamParticipantModal.maxLength", { max: 20 }));
                    return;
                  }
                  setInviteQuery(capitalizeWords(e.target.value));
                  setInviteQueryError(null);
                  setShowInviteResults(true);
                }}
                onFocus={() => inviteQuery.trim().length >= 2 && setShowInviteResults(true)}
                className="2xl:mt-[0.208vw] mt-1 rtl:dir-rtl rtl:text-right w-full 2xl:text-[1.2vw]/none md:text-lg/none text-base/none font-bold skew-x-[10deg] text-black-300 placeholder:text-black-400 focus:outline-none transition-all disabled:opacity-50 bg-transparent"
              />
              {isInviteSearching && (
                <div className="absolute right-4 rtl:right-auto rtl:left-4 top-1/2 -translate-y-1/2">
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-secondary border-t-transparent"></div>
                </div>
              )}
            </div>
            {inviteQueryError && (
              <p className="text-red-500 lg:text-[0.9vw] text-xs lg:mt-[0.208vw] mt-1 font-bold">{inviteQueryError}</p>
            )}

            {showInviteResults && inviteQuery.trim().length >= 2 && (
              <div className="absolute top-full left-0 right-0 mt-2 2xl:rounded-[0.417vw] rounded-lg 2xl:border-[0.104vw] border-[1.5px] border-black-300 bg-primary-ivory 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706] overflow-hidden z-20 max-h-[300px] overflow-y-auto scroll-width-small">
                <div className="2xl:p-[1vw] p-4">
                  {isInviteSearching ? (
                    <div className="py-8 flex items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-orange border-t-transparent"></div>
                    </div>
                  ) : filteredInviteList.length === 0 ? (
                    <p className="2xl:text-[1.05vw] text-base text-black-400 py-4 text-center">
                      {t("createGame.teamParticipantModal.noRecordFound", { name: inviteQuery })}
                    </p>
                  ) : (
                    <div className="2xl:space-y-[0.5vw] space-y-2">
                      {filteredInviteList.map((user) => {
                        const isAlreadyAdded = isParticipantInCurrentTeam(user.id, user.email, user.phone_number);
                        const characterUrl = getPortraitAvatarUrl(user?.profile_image, user?.gender);
                        return (
                          <div
                            key={user.id}
                            title={isAlreadyAdded ? t("createGame.teamParticipantModal.added") || "Already added" : ""}
                            className={`w-full flex items-center 2xl:gap-[0.75vw] gap-3 2xl:p-[0.625vw] p-2 2xl:rounded-[0.26vw] rounded-[5px] transition-all duration-200 2xl:border-[0.104vw] border-[1.5px] border-black-300 ${isAlreadyAdded
                              ? "bg-[#E7E6E6] opacity-60 cursor-not-allowed"
                              : "bg-primary-ivory hover:bg-primary-yellow hover:border-primary-orange 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]"
                              }`}
                          >
                            <div className="flex items-center 2xl:gap-[0.75vw] gap-3 flex-1 min-w-0">
                              <div className="2xl:w-[2.6vw] 2xl:h-[2.6vw] w-11 h-11 2xl:rounded-[0.26vw] rounded-[5px] bg-[#97A4F2] flex items-center justify-center text-white font-bold 2xl:text-[0.938vw] text-base overflow-hidden 2xl:border-[0.104vw] border-[1.5px] border-black-300 flex-shrink-0">
                                {
                                  characterUrl ? (
                                    <img src={characterUrl} alt={user.full_name} className="h-full w-full object-cover" />
                                  )
                                    : user.full_name?.charAt(0)?.toUpperCase() || "?"
                                }
                              </div>
                              <div className="flex-1 text-left min-w-0">
                                <p className="2xl:text-[1.02vw] text-base font-semibold text-primary-orange truncate">{user.full_name}</p>
                                {/* <p className="2xl:text-[0.729vw] text-xs text-black-400 truncate">{user.email}</p> */}
                              </div>
                              {!isAlreadyAdded && (
                                <button
                                  type="button"
                                  onClick={() => addAsFriendFromSearch(user)}
                                  className="2xl:h-[1.875vw] h-8 2xl:px-[0.625vw] px-3 2xl:rounded-[0.26vw] rounded-[5px] bg-[#E7E6E6] hover:bg-primary-yellow text-black-300 2xl:text-[0.729vw] text-xs font-bold border border-black-300 flex items-center justify-center flex-shrink-0"
                                >
                                  {t("createGame.teamParticipantModal.addAsFriend", { defaultValue: "Add as a friend" })}
                                </button>
                              )}
                              {!isAlreadyAdded && (
                                <button
                                  type="button"
                                  onClick={() => addUserFromSearch(user)}
                                  className="2xl:w-[1.875vw] 2xl:h-[1.875vw] w-8 h-8 2xl:rounded-[0.26vw] rounded-[5px] bg-primary-orange flex items-center justify-center flex-shrink-0"
                                  aria-label={t("createGame.teamParticipantModal.add") || "Add"}
                                >
                                  <svg className="2xl:w-[0.833vw] 2xl:h-[0.833vw] w-4 h-4 text-white skew-x-[10deg]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                                  </svg>
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Guest Tab */}
        {activeMainTab === "guest" && (
          <div className="2xl:mb-[1.5vw] mb-4 2xl:p-[1vw] p-4 2xl:rounded-[0.417vw] rounded-lg 2xl:border-[0.104vw] border-[1.5px] border-black-300 bg-primary-ivory">
            <div className="2xl:space-y-[1vw] space-y-4">
              <div>
                <label className="block 2xl:text-[1.05vw] text-base font-bold text-black-700 2xl:mb-[0.5vw] mb-2">
                  {t("createGame.teamParticipantModal.fullName")}
                </label>
                <input
                  type="text"
                  placeholder={t("createGame.teamParticipantModal.fullNamePlaceholder")}
                  dir={isRtl ? "rtl" : "ltr"}
                  value={guestFullName}
                  maxLength={21}
                  onChange={(e) => {
                    if (e.target.value?.length > 20) return;
                    setGuestFullName(capitalizeWords(e.target.value));
                  }}
                  className="w-full 2xl:py-[0.6vw] py-2 2xl:px-[1vw] px-4 2xl:rounded-[0.26vw] rounded-[5px] 2xl:border-[0.104vw] border-[1.5px] border-black-300 skew-x-[-10deg] 2xl:text-[1.1vw] text-lg font-bold text-black-700 placeholder:text-black-400 focus:outline-none focus:border-primary-orange"
                />
              </div>
              <div>
                <label className="block 2xl:text-[1.05vw] text-base font-bold text-black-700 2xl:mb-[0.5vw] mb-2">
                  {t("createGame.teamParticipantModal.selectGenderPlaceholder")}
                </label>
                <div className="flex items-center 2xl:gap-[0.625vw] gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedGuestGender("male");
                      setGuestGenderError(null);
                    }}
                    className={`2xl:px-[1.042vw] px-4 2xl:py-[0.521vw] py-2 font-bold 2xl:text-[0.95vw] text-base 2xl:rounded-[0.26vw] rounded-[5px] border-2 border-black-300 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706] hover:-translate-y-0.5 transition-transform duration-200 skew-x-[-10deg] ${selectedGuestGender === "male" ? "bg-primary-orange text-white" : "bg-white text-black-700"
                      }`}
                  >
                    <span className="skew-x-[10deg]">{t("createGame.teamParticipantModal.male") || "Male"}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedGuestGender("female");
                      setGuestGenderError(null);
                    }}
                    className={`2xl:px-[1.042vw] px-4 2xl:py-[0.521vw] py-2 font-bold 2xl:text-[0.95vw] text-base 2xl:rounded-[0.26vw] rounded-[5px] border-2 border-black-300 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706] hover:-translate-y-0.5 transition-transform duration-200 skew-x-[-10deg] ${selectedGuestGender === "female" ? "bg-primary-orange text-white" : "bg-white text-black-700"
                      }`}
                  >
                    <span className="skew-x-[10deg]">{t("createGame.teamParticipantModal.female") || "Female"}</span>
                  </button>
                </div>
                {guestGenderError && (
                  <p className="text-red-500 2xl:text-[0.95vw] text-base font-bold 2xl:mt-[0.5vw] mt-2">{guestGenderError}</p>
                )}
              </div>
              <button
                type="button"
                onClick={addGuestFromForm}
                className="w-full 2xl:py-[0.833vw] py-3 2xl:px-[1.25vw] px-5 font-bold 2xl:text-[1.02vw] text-lg 2xl:rounded-[0.26vw] rounded-[5px] bg-[linear-gradient(180deg,#D76A4D_0%,#B35840_101.89%)] text-white border-2 border-black-300 skew-x-[-10deg] 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706] hover:-translate-y-0.5 transition-transform duration-200"
              >
                <span className="skew-x-[10deg]">{t("createGame.teamParticipantModal.addAsGuest")}</span>
              </button>
            </div>
          </div>
        )}

        <div className="2xl:space-y-[1vw] xl:space-y-4 sm:space-y-3 space-y-5 2xl:min-h-[10vw]  md:min-h-[300px] min-h-[100px]">
          {/* Add New Participant Row */}
          <div className="relative grid sm:grid-cols-3 grid-cols-2 max-[420px]:grid-cols-1 2xl:gap-[0.7vw] gap-3 z-10">
            {/* Added participants list (from search/manual add) */}
            {localParticipants.map((participant: Participant, index: number) => {
              const characterUrl = getPortraitAvatarUrl(participant?.profile_image, participant?.gender);
              return (
                <div
                  key={participant.id}
                  // onClick={() => handleParticipantClick(participant.id)}
                  className="col-span-1 2xl:border-[0.104vw] border-2 border-black-300 bg-primary-ivory 2xl:p-[0.5vw] p-4 2xl:rounded-[0.417vw] rounded-lg cursor-pointer relative"
                >
                  {/* Remove Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setParticipantToDelete({ id: participant.id, name: participant.full_name });
                    }}
                    className="absolute 2xl:top-[0.416vw] top-1 2xl:end-[0.416vw] end-1 2xl:w-[1.667vw] 2xl:h-[1.667vw] w-6 h-6 flex items-center justify-center skew-x-[-10deg] bg-primary-orange 2xl:border-[0.104vw] border-[1.5px] border-black-300 2xl:rounded-[0.26vw] rounded-[5px] 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706] transition-all z-10 cursor-pointer hover:-translate-y-0.5"
                    aria-label="Remove participant"
                    title={t("createGame.teamParticipantModal.remove") || "Remove"}
                  >
                    <svg
                      className="2xl:w-[0.833vw] 2xl:h-[0.833vw] w-4 h-4 text-white skew-x-[10deg]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>

                  {/* Avatar */}
                  <div
                    className="mx-auto 2xl:w-[2.5vw] xl:w-[40px] lg:w-10 w-10 2xl:min-w-[2.5vw] xl:min-w-[40px] lg:min-w-10 min-w-10 2xl:h-[2.5vw] xl:h-[40px] lg:h-10 h-10 aspect-square overflow-hidden
                    bg-[#97A4F2] skew-x-[-10deg]
                    2xl:border-[0.104vw] border-[1.5px] 2xl:rounded-[0.26vw] rounded-[5px] 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]
                    
                    
                     flex items-center justify-center text-white font-bold 2xl:text-[0.938vw] text-base  border-black-300                    "
                  >
                    {
                      characterUrl ? (
                        <img src={characterUrl} alt={participant.full_name} className="h-full w-full object-cover" />
                      )
                        : participant.full_name?.charAt(0)?.toUpperCase() || "?"
                    }
                  </div>

                  {/* Name */}
                  <h2 className="font-bold text-primary-rose 2xl:text-[0.833vw]/tight text-sm/tight 2xl:pt-[0.6vw] pt-2 2xl:pb-[0.4vw] pb-1.5 text-center w-full">
                    {participant.full_name}{" "}
                    {participant.gender &&
                      `| ${participant.gender === "male"
                        ? t("createGame.teamParticipantModal.male")
                        : t("createGame.teamParticipantModal.female")
                      }`}
                  </h2>


                  {/* Status Button */}
                  <div className="2xl:w-[7.292vw] md:w-[140px] w-30 2xl:h-[2.083vw] h-10 mx-auto relative border-1 border-black-300 rounded-md bg-[#C9D863]">
                    {/* <svg
                      className="h-full w-full"
                      width="140"
                      height="40"
                      viewBox="0 0 140 40"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M99.5752 0.5H114.268L126.766 0.995117V0.996094L133.87 1.30762C134.383 1.3301 134.787 1.75249 134.787 2.26562V2.46094C134.787 3.29135 135.379 4.00406 136.195 4.15625C136.538 4.22015 136.787 4.51933 136.787 4.86816V5.19434C136.787 6.02457 137.379 6.73647 138.195 6.88867C138.538 6.95254 138.787 7.25196 138.787 7.60059V9.04395C138.787 9.46732 138.444 9.81055 138.021 9.81055H137.151C135.99 9.81055 135.36 11.1696 136.11 12.0557C136.273 12.2479 136.196 12.5438 135.96 12.6318L135.769 12.7031C135.179 12.923 134.787 13.4867 134.787 14.1162V15.1533C133.968 15.1643 133.517 16.1147 134.033 16.7559L134.681 17.5596C134.749 17.6448 134.787 17.7519 134.787 17.8613V18.001C134.787 18.8471 135.343 19.5484 136.096 19.8018C135.426 20.0568 134.972 20.4117 134.711 20.8486C134.32 21.5042 134.448 22.1967 134.675 22.6729C134.817 22.9716 135.049 23.1788 135.244 23.3242L137.792 25.2236C137.899 25.3033 137.859 25.4717 137.729 25.4961C137.183 25.5978 136.787 26.0739 136.787 26.6289V27.8418C136.787 28.0798 136.731 28.3147 136.624 28.5273L135.34 31.0801C134.952 31.8512 134.985 32.7673 135.427 33.5088L136.572 35.4287C136.713 35.6649 136.787 35.935 136.787 36.21V37.5713C136.787 38.3856 136.147 39.056 135.334 39.0947L127.06 39.4863C126.912 39.4933 126.764 39.479 126.62 39.4434L125.148 39.0771C124.949 39.0277 124.744 39.0029 124.539 39.0029H114.305L111.328 38.7568L111.308 38.7549H102.77L102.752 38.7559L99.2656 39.0029H93.252L93.2168 39.0078L89.751 39.5H80.7969L71.8145 39.0039L71.8008 39.0029H60.7988L50.2988 38.7549H50.2744L40.2744 39.0029L40.2666 39.0039H40.2578L31.7744 39.5H25.3086L18.3223 39.0039L18.3047 39.0029H4.20312C3.56282 39.0029 2.99066 38.6032 2.77051 38.002L1.09766 33.4326C0.935433 32.9893 0.987935 32.4953 1.23926 32.0957L2.48438 30.1152C3.03129 29.2455 2.99712 28.1312 2.39746 27.2969L1.50098 26.0498C1.13405 25.5393 1.11763 24.8554 1.45996 24.3281L2.42383 22.8447C3.00832 21.9444 2.95962 20.7728 2.30273 19.9238L1.43262 18.7988C1.08546 18.3503 1.01506 17.7463 1.25098 17.2305L3.05859 13.2803C3.20939 12.9506 3.28711 12.592 3.28711 12.2295V4.7334C3.28701 3.77469 2.74426 2.89839 1.88574 2.47168L0.902344 1.9834C0.752484 1.90893 0.632771 1.7846 0.564453 1.63184C0.34356 1.13784 0.72542 0.5854 1.26562 0.617188L18.2578 1.61719L18.2725 1.61816H33.3027L33.3184 1.61719L43.3027 0.99707H67.2871V0.996094L76.2666 1.36914H76.2754L86.7754 1.61816H86.7998L96.04 1.37598C96.2556 1.3703 96.4696 1.33738 96.6768 1.27734L99.1504 0.560547C99.2883 0.520593 99.4316 0.5 99.5752 0.5Z"
                        fill="#C9D863"
                        stroke="#110706"
                      />
                    </svg> */}

                    <span
                      style={{ color: "#110706" }}
                      className="absolute inset-0 z-20 flex items-center justify-center 2xl:pt-[0.3vw] pt-1  font-bold text-center font-garamond 2xl:text-[0.938vw]/tight text-base/tight"
                    >
                      {participant.joinStatus === "invited"
                        ? t("createGame.teamParticipantModal.invited")
                        : t("createGame.teamParticipantModal.joined")}
                    </span>
                  </div>
                  {participant.isCaptain && (
                    <div
                      className={`mt-2 w-full rounded-md border border-black-300 py-1 text-xs font-bold transition-colors ${participant.isCaptain
                        ? "bg-primary-yellow text-black-300 hover:bg-primary-yellow"
                        : "bg-[#E7E6E6] text-black-400"
                        }`}
                    >
                      <p className="text-center text-[11px] font-bold uppercase tracking-wide 2xl:text-[0.625vw]">
                        {t("createGame.teamParticipantModal.captain")}
                      </p>
                    </div>
                  )}
                  {
                    !participant.isCaptain &&
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        assignCaptain(participant.id);
                      }}
                      disabled={participant.isCaptain}
                      className={`mt-2 w-full rounded-md border border-black-300 py-1 text-xs font-bold transition-colors ${participant.isCaptain
                        ? "cursor-default bg-[#E7E6E6] text-black-400"
                        : "bg-primary-yellow text-black-300 hover:bg-[#ffe86a]"
                        }`}
                    >
                      {t("createGame.teamParticipantModal.assignCaptain") || "Assign Captain"}
                    </button>
                  }

                </div>
              );
            })}
          </div>
        </div>
      </Modal>
      <DeleteParticipantModal
        isOpen={participantToDelete !== null}
        onClose={handleCancelDeleteParticipant}
        onConfirm={handleConfirmDeleteParticipant}
        participantName={participantToDelete?.name || ""}
      />
      <TeamAchievementModal
        isOpen={isAchievementModalOpen}
        onClose={() => {
          setIsAchievementModalOpen(false);
          setSelectedParticipantId(null);
        }}
        participantId={selectedParticipantId}
      />
    </>
  );
}

export default TeamParticipantModals;
